import { useMemo } from 'react';
import { useBatchAuthorization, BatchAuthRequest } from './useBatchAuthorization';

export type AppAuthorization = {
  pages: { authorized: boolean; loading: boolean };
  workflows: { authorized: boolean; loading: boolean };
  tasks: { authorized: boolean; loading: boolean };
  pluginRoute: { authorized: boolean; loading: boolean };
  loading: boolean;
  error: any;
};

/**
 * Batches all application-level authorization checks into a single PDP call.
 * This replaces multiple individual useAuthorization calls in App.tsx and LayoutShell.tsx.
 * 
 * Reduces 6 sequential authorization calls to 1 bulk call on every page load.
 */
export function useAppAuthorization(): AppAuthorization {
  const requests: BatchAuthRequest[] = useMemo(() => [
    { 
      key: 'pages:view_all', 
      resource: { type: 'pages', id: 'NoResource', properties: { pdp_application: 'experience' } }, 
      action: { name: 'view_all' }, 
      context: {
        pdp_application: 'experience',
        decision_intent: 'cache_warmup_probe',
        decision_surface: 'preload',
        decision_origin: 'experience_frontend',
        decision_is_background: true,
      }
    },
    { 
      key: 'workflows:view_all', 
      resource: { type: 'workflows', id: 'NoResource', properties: { pdp_application: 'experience' } }, 
      action: { name: 'view_all' }, 
      context: {
        pdp_application: 'experience',
        decision_intent: 'cache_warmup_probe',
        decision_surface: 'preload',
        decision_origin: 'experience_frontend',
        decision_is_background: true,
      }
    },
    { 
      key: 'tasks:view_all', 
      resource: { type: 'tasks', id: 'NoResource', properties: { pdp_application: 'experience' } }, 
      action: { name: 'view_all' }, 
      context: {
        pdp_application: 'experience',
        decision_intent: 'cache_warmup_probe',
        decision_surface: 'preload',
        decision_origin: 'experience_frontend',
        decision_is_background: true,
      }
    },
    { 
      key: 'experience.route:view',
      resource: { type: 'experience.route', id: 'route:experience:/', properties: { pdp_application: 'experience' } },
      action: { name: 'view' }, 
      context: {
        pdp_application: 'experience',
        decision_intent: 'cache_warmup_probe',
        decision_surface: 'preload',
        decision_origin: 'experience_frontend',
        decision_is_background: true,
      }
    },
  ], []);

  const { decisions, loading, error } = useBatchAuthorization(requests);

  return {
    pages: { 
      authorized: decisions?.get('pages:view_all') ?? false, 
      loading 
    },
    workflows: { 
      authorized: decisions?.get('workflows:view_all') ?? false, 
      loading 
    },
    tasks: { 
      authorized: decisions?.get('tasks:view_all') ?? false, 
      loading 
    },
    pluginRoute: { 
      authorized: decisions?.get('experience.route:view') ?? false,
      loading 
    },
    loading,
    error
  };
}

